<template>
  <div class="profile-page container py-5">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10">
        <div class="card profile-card">
          <div class="head">
            <div>
              <h2 v-tx="'ProfileNurseTitle'">{{ tx('title') }}</h2>
              <p class="muted" v-tx="'ProfileNurseSubtitle'">{{ tx('subtitle') }}</p>
            </div>
            <button type="button" class="ghost" @click="logout" v-tx="'ProfileNurseLogout'">{{ tx('logout') }}</button>
          </div>

          <div v-if="!form.isApproved" class="banner pending" v-tx="'ProfileNurseBannerPending'">
            ⏳ {{ tx('bannerPending') }}
          </div>
          <div v-else class="banner approved" v-tx="'ProfileNurseBannerApproved'">
            ✓ {{ tx('bannerApproved') }}
          </div>

          <div class="tabs">
            <button type="button" :class="{ active: activeTab === 'cv' }" @click="activeTab = 'cv'" v-tx="'ProfileNurseTabCv'">{{ tx('tabCv') }}</button>
            <button type="button" :class="{ active: activeTab === 'documents' }" @click="activeTab = 'documents'" v-tx="'ProfileNurseTabDocuments'">{{ tx('tabDocuments') }}</button>
          </div>

          <div v-if="loading" class="status" v-tx="'ProfileNurseLoading'">{{ tx('loading') }}</div>

          <form v-if="!loading && activeTab === 'cv'" @submit.prevent="save">
            <!-- 1. Şəxsi məlumatlar -->
            <section class="section">
              <h3 v-tx="'ProfileNurseSec1'">{{ tx('sec1') }}</h3>
              <div class="photo-row">
                <div class="photo-preview">
                  <img v-if="photoUrl" :src="photoUrl" :alt="tx('photoLabel')" />
                  <span v-else class="ph">{{ (form.name || '?').charAt(0).toUpperCase() }}{{ (form.surname || '').charAt(0).toUpperCase() }}</span>
                </div>
                <div class="photo-actions">
                  <span class="photo-label" v-tx="'ProfileNursePhotoLabel'">{{ tx('photoLabel') }}</span>
                  <span class="photo-hint" v-tx="'ProfileNursePhotoHint'">{{ tx('photoHint') }}</span>
                  <input ref="photoInput" type="file" accept="image/*" class="hidden-file" @change="onPhotoChange" />
                  <button type="button" class="photo-btn" :disabled="photoUploading" @click="triggerPhoto" v-tx="'ProfileNursePhotoUpload'">
                    {{ photoUploading ? tx('photoUploading') : (photoUrl ? tx('photoChange') : tx('photoUpload')) }}
                  </button>
                </div>
              </div>
              <div class="grid">
                <label class="field"><span v-tx="'ProfileNurseName'">{{ tx('name') }} *</span><input type="text" v-model="form.name" required maxlength="100" /></label>
                <label class="field"><span v-tx="'ProfileNurseSurname'">{{ tx('surname') }} *</span><input type="text" v-model="form.surname" required maxlength="100" /></label>
                <label class="field"><span v-tx="'ProfileNurseGender'">{{ tx('gender') }}</span>
                  <select v-model="form.gender">
                    <option value="">{{ tx('genderEmpty') }}</option>
                    <option value="male">{{ tx('genderMale') }}</option>
                    <option value="female">{{ tx('genderFemale') }}</option>
                  </select>
                </label>
                <label class="field"><span v-tx="'ProfileNurseBirthDate'">{{ tx('birthDate') }}</span><input type="date" v-model="form.birthDate" /></label>
                <label class="field"><span v-tx="'ProfileNurseCitizenship'">{{ tx('citizenship') }}</span><input type="text" v-model="form.citizenship" maxlength="100" /></label>
                <label class="field"><span v-tx="'ProfileNursePhone'">{{ tx('phone') }}</span><input type="tel" v-model="form.phone" maxlength="50" /></label>
                <label class="field"><span v-tx="'ProfileNurseEmail'">{{ tx('email') }}</span><input type="email" :value="email" disabled /></label>
              </div>
              <label class="field full"><span v-tx="'ProfileNurseAddress'">{{ tx('address') }}</span><textarea v-model="form.address" maxlength="500" rows="2"></textarea></label>
            </section>

            <!-- 2. Təhsil -->
            <section class="section">
              <div class="section-head">
                <h3 v-tx="'ProfileNurseSec2'">{{ tx('sec2') }}</h3>
                <button type="button" class="add" @click="addEducation" v-tx="'ProfileNurseAddBtn'">{{ tx('addBtn') }}</button>
              </div>
              <div v-if="!form.educations.length" class="empty" v-tx="'ProfileNurseEmptyEducation'">{{ tx('emptyEducation') }}</div>
              <div v-for="(e, idx) in form.educations" :key="idx" class="row-block">
                <div class="grid">
                  <label class="field"><span v-tx="'ProfileNurseInstitution'">{{ tx('institution') }} *</span><input type="text" v-model="e.institutionName" required maxlength="300" /></label>
                  <label class="field"><span v-tx="'ProfileNurseProfession'">{{ tx('profession') }}</span>
                    <select v-model="e.professionId">
                      <option :value="null">{{ tx('professionEmpty') }}</option>
                      <option v-for="p in professions" :key="p.id" :value="p.id">{{ p.name }}</option>
                    </select>
                  </label>
                  <label class="field"><span v-tx="'ProfileNurseStartDate'">{{ tx('startDate') }}</span><input type="date" v-model="e.startDate" /></label>
                  <label class="field"><span v-tx="'ProfileNurseEndDate'">{{ tx('endDate') }}</span><input type="date" v-model="e.endDate" /></label>
                </div>
                <button type="button" class="remove" @click="form.educations.splice(idx, 1)" v-tx="'ProfileNurseRemoveBtn'">{{ tx('removeBtn') }}</button>
              </div>
            </section>

            <!-- 3. İş təcrübəsi -->
            <section class="section">
              <div class="section-head">
                <h3 v-tx="'ProfileNurseSec3'">{{ tx('sec3') }}</h3>
                <button type="button" class="add" @click="addWork" v-tx="'ProfileNurseAddBtn'">{{ tx('addBtn') }}</button>
              </div>
              <div v-if="!form.workExperiences.length" class="empty" v-tx="'ProfileNurseEmptyWork'">{{ tx('emptyWork') }}</div>
              <div v-for="(w, idx) in form.workExperiences" :key="idx" class="row-block">
                <div class="grid">
                  <label class="field"><span v-tx="'ProfileNurseWorkplace'">{{ tx('workplace') }} *</span><input type="text" v-model="w.workplace" required maxlength="300" /></label>
                  <label class="field"><span v-tx="'ProfileNursePosition'">{{ tx('position') }} *</span><input type="text" v-model="w.position" required maxlength="200" /></label>
                  <label class="field"><span v-tx="'ProfileNurseStartDate'">{{ tx('startDate') }}</span><input type="date" v-model="w.startDate" /></label>
                  <label class="field"><span v-tx="'ProfileNurseEndDate'">{{ tx('endDate') }}</span><input type="date" v-model="w.endDate" /></label>
                </div>
                <label class="field full"><span v-tx="'ProfileNurseDuties'">{{ tx('duties') }}</span><textarea v-model="w.duties" rows="3"></textarea></label>
                <button type="button" class="remove" @click="form.workExperiences.splice(idx, 1)" v-tx="'ProfileNurseRemoveBtn'">{{ tx('removeBtn') }}</button>
              </div>
            </section>

            <!-- 4. Dil bilikləri -->
            <section class="section">
              <div class="section-head">
                <h3 v-tx="'ProfileNurseSec4'">{{ tx('sec4') }}</h3>
                <button type="button" class="add" @click="addLanguage" v-tx="'ProfileNurseAddBtn'">{{ tx('addBtn') }}</button>
              </div>
              <div v-if="!form.languages.length" class="empty" v-tx="'ProfileNurseEmptyLanguages'">{{ tx('emptyLanguages') }}</div>
              <div v-for="(l, idx) in form.languages" :key="idx" class="row-block">
                <div class="grid">
                  <label class="field"><span v-tx="'ProfileNurseLanguage'">{{ tx('language') }} *</span><input type="text" v-model="l.languageName" required maxlength="100" /></label>
                  <label class="field"><span v-tx="'ProfileNurseLevel'">{{ tx('level') }} *</span>
                    <select v-model="l.level" required>
                      <option value="">{{ tx('levelEmpty') }}</option>
                      <option value="A1">A1 — Beginner</option>
                      <option value="A2">A2 — Elementary</option>
                      <option value="B1">B1 — Intermediate</option>
                      <option value="B2">B2 — Upper Intermediate</option>
                      <option value="C1">C1 — Advanced</option>
                      <option value="C2">C2 — Proficient</option>
                    </select>
                  </label>
                </div>
                <button type="button" class="remove" @click="form.languages.splice(idx, 1)" v-tx="'ProfileNurseRemoveBtn'">{{ tx('removeBtn') }}</button>
              </div>
            </section>

            <!-- 5. Sertifikatlar -->
            <section class="section">
              <div class="section-head">
                <h3 v-tx="'ProfileNurseSec5'">{{ tx('sec5') }}</h3>
                <button type="button" class="add" @click="addCertificate" v-tx="'ProfileNurseAddBtn'">{{ tx('addBtn') }}</button>
              </div>
              <div v-if="!form.certificates.length" class="empty" v-tx="'ProfileNurseEmptyCertificates'">{{ tx('emptyCertificates') }}</div>
              <div v-for="(c, idx) in form.certificates" :key="idx" class="row-block">
                <div class="grid">
                  <label class="field"><span v-tx="'ProfileNurseCertTitle'">{{ tx('certTitle') }} *</span><input type="text" v-model="c.title" required maxlength="300" /></label>
                  <label class="field"><span v-tx="'ProfileNurseCertIssuedBy'">{{ tx('certIssuedBy') }}</span><input type="text" v-model="c.issuedBy" maxlength="300" /></label>
                  <label class="field"><span v-tx="'ProfileNurseCertDate'">{{ tx('certDate') }}</span><input type="date" v-model="c.issuedDate" /></label>
                </div>
                <button type="button" class="remove" @click="form.certificates.splice(idx, 1)" v-tx="'ProfileNurseRemoveBtn'">{{ tx('removeBtn') }}</button>
              </div>
            </section>

            <!-- 6. Bacarıqlar -->
            <section class="section">
              <div class="section-head">
                <h3 v-tx="'ProfileNurseSec6'">{{ tx('sec6') }}</h3>
                <button type="button" class="add" @click="addSkill" v-tx="'ProfileNurseAddBtn'">{{ tx('addBtn') }}</button>
              </div>
              <div v-if="!form.skills.length" class="empty" v-tx="'ProfileNurseEmptySkills'">{{ tx('emptySkills') }}</div>
              <div v-for="(s, idx) in form.skills" :key="idx" class="row-block">
                <label class="field full"><span v-tx="'ProfileNurseSkill'">{{ tx('skill') }} *</span><input type="text" v-model="s.description" required maxlength="500" /></label>
                <button type="button" class="remove" @click="form.skills.splice(idx, 1)" v-tx="'ProfileNurseRemoveBtn'">{{ tx('removeBtn') }}</button>
              </div>
            </section>

            <button type="submit" class="primary save-btn" :disabled="saving" v-tx="'ProfileNurseSaveBtn'">
              {{ saving ? tx('savingBtn') : tx('saveBtn') }}
            </button>
          </form>

          <div v-if="!loading && activeTab === 'documents'" class="docs-tab">
            <p class="muted small-info" v-tx="'ProfileNurseDocsInfo'">{{ tx('docsInfo') }}</p>
            <div v-if="!documentTypes.length" class="status" v-tx="'ProfileNurseDocsEmpty'">{{ tx('docsEmpty') }}</div>
            <ul class="docs-list">
              <li v-for="dt in documentTypes" :key="dt.id" class="doc-row">
                <div class="info">
                  <div class="title-row">
                    <strong>{{ dt.name }}</strong>
                    <span v-if="dt.type" class="muted small"> · {{ dt.type }}</span>
                    <span
                      v-if="currentDocFor(dt.id)"
                      class="status-badge"
                      :class="statusClass(currentDocFor(dt.id).status)"
                    >
                      {{ statusLabel(currentDocFor(dt.id).status) }}
                    </span>
                  </div>
                  <div v-if="currentDocFor(dt.id)" class="existing">
                    <span v-tx="'ProfileNurseDocUploaded'">{{ tx('docUploaded') }}</span>
                    <a :href="currentDocFor(dt.id).fileUrl" target="_blank" rel="noopener">{{ currentDocFor(dt.id).fileName }}</a>
                    <button type="button" class="link-danger" @click="removeDoc(currentDocFor(dt.id).id)" v-tx="'ProfileNurseRemoveBtn'">{{ tx('removeBtn') }}</button>
                  </div>
                  <div v-if="currentDocFor(dt.id) && currentDocFor(dt.id).adminNote" class="admin-note">
                    <span class="lbl">IGAplus:</span>
                    <span>{{ currentDocFor(dt.id).adminNote }}</span>
                  </div>
                </div>
                <label class="upload-btn" v-tx="'ProfileNurseDocUploadBtn'">
                  <input type="file" :disabled="uploadingType === dt.id" @change="onDocFile($event, dt.id)" />
                  {{ uploadingType === dt.id ? tx('docUploading') : (currentDocFor(dt.id) ? tx('docUpdateBtn') : tx('docUploadBtn')) }}
                </label>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

function emptyEducation() { return { institutionName: "", professionId: null, startDate: "", endDate: "" }; }
function emptyWork() { return { workplace: "", position: "", startDate: "", endDate: "", duties: "" }; }
function emptyLanguage() { return { languageName: "", level: "" }; }
function emptyCertificate() { return { title: "", issuedBy: "", issuedDate: "" }; }
function emptySkill() { return { description: "" }; }

function isoDate(v) {
  if (!v) return null;
  // already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  try { return new Date(v).toISOString().split("T")[0]; } catch { return null; }
}

const T = {
  az: {
    title: "CV-m", subtitle: "Europass tipli forma — bütün bölmələri doldurun və yadda saxlayın.", logout: "Çıxış",
    bannerPending: "Profiliniz admin tərəfindən təsdiq gözləyir. Təsdiqdən sonra klinikalar onu görəcək.",
    bannerApproved: "Profiliniz təsdiqlidir.",
    tabCv: "CV", tabDocuments: "Sənədlər", loading: "Yüklənir…",
    sec1: "1. Şəxsi məlumatlar", sec2: "2. Təhsil", sec3: "3. İş təcrübəsi", sec4: "4. Dil bilikləri", sec5: "5. Sertifikatlar", sec6: "6. Bacarıqlar",
    photoLabel: "Profil şəkli", photoHint: "CV endirildikdə bu şəkil görünəcək. Kvadrat / portret şəkil tövsiyə olunur.",
    photoUploading: "Yüklənir…", photoChange: "Şəkli dəyiş", photoUpload: "Şəkil yüklə",
    name: "Ad", surname: "Soyad", gender: "Cins", genderEmpty: "— seçilməyib —", genderMale: "Kişi", genderFemale: "Qadın",
    birthDate: "Doğum tarixi", citizenship: "Vətəndaşlıq", phone: "Telefon", email: "Email", address: "Ünvan",
    addBtn: "+ Əlavə et",
    emptyEducation: "Heç bir təhsil əlavə edilməyib.", emptyWork: "Heç bir iş təcrübəsi əlavə edilməyib.",
    emptyLanguages: "Heç bir dil əlavə edilməyib.", emptyCertificates: "Heç bir sertifikat əlavə edilməyib.", emptySkills: "Heç bir bacarıq əlavə edilməyib.",
    institution: "Müəssisə adı", profession: "İxtisas", professionEmpty: "— seçilməyib —",
    startDate: "Başlama tarixi", endDate: "Bitmə tarixi", workplace: "İş yeri", position: "Vəzifə", duties: "Görülən işlər",
    language: "Dil", level: "Səviyyə", levelEmpty: "— seçin —",
    certTitle: "Başlıq", certIssuedBy: "Verən təşkilat", certDate: "Tarix", skill: "Bacarıq",
    removeBtn: "Sil", saveBtn: "CV-ni yadda saxla", savingBtn: "Yadda saxlanır…",
    docsInfo: "Aşağıdakı siyahıdan hər sənəd növü üçün öz faylınızı yükləyin. Eyni növ üçün yeni fayl yükləsəniz köhnəsini əvəz edir.",
    docsEmpty: "Hələ sənəd növü təyin edilməyib.", docUploaded: "Yüklənmiş:",
    docUploadBtn: "Fayl seç", docUpdateBtn: "Yenilə", docUploading: "Yüklənir…",
    statusApproved: "Təsdiqli", statusRejected: "İmtina edildi", statusPending: "Gözləyir",
    toastPhotoInvalid: "Yalnız şəkil faylı yükləyə bilərsiniz", toastPhotoUpdated: "Profil şəkli yeniləndi",
    toastSaved: "CV yadda saxlanıldı", toastDocUploaded: "Yükləndi", toastDocDeleted: "Silindi",
  },
  de: {
    title: "Mein Lebenslauf", subtitle: "Europass-Formular — füllen Sie alle Abschnitte aus und speichern Sie.", logout: "Abmelden",
    bannerPending: "Ihr Profil wartet auf die Bestätigung durch den Administrator. Nach der Bestätigung sehen es die Kliniken.",
    bannerApproved: "Ihr Profil ist bestätigt.",
    tabCv: "Lebenslauf", tabDocuments: "Dokumente", loading: "Wird geladen…",
    sec1: "1. Persönliche Angaben", sec2: "2. Ausbildung", sec3: "3. Berufserfahrung", sec4: "4. Sprachkenntnisse", sec5: "5. Zertifikate", sec6: "6. Fähigkeiten",
    photoLabel: "Profilbild", photoHint: "Beim Herunterladen des Lebenslaufs wird dieses Bild angezeigt. Quadratisches / Porträtbild empfohlen.",
    photoUploading: "Wird hochgeladen…", photoChange: "Bild ändern", photoUpload: "Bild hochladen",
    name: "Vorname", surname: "Nachname", gender: "Geschlecht", genderEmpty: "— nicht ausgewählt —", genderMale: "Männlich", genderFemale: "Weiblich",
    birthDate: "Geburtsdatum", citizenship: "Staatsangehörigkeit", phone: "Telefon", email: "E-Mail", address: "Adresse",
    addBtn: "+ Hinzufügen",
    emptyEducation: "Keine Ausbildung hinzugefügt.", emptyWork: "Keine Berufserfahrung hinzugefügt.",
    emptyLanguages: "Keine Sprachen hinzugefügt.", emptyCertificates: "Keine Zertifikate hinzugefügt.", emptySkills: "Keine Fähigkeiten hinzugefügt.",
    institution: "Institution", profession: "Beruf", professionEmpty: "— nicht ausgewählt —",
    startDate: "Beginn", endDate: "Ende", workplace: "Arbeitsstelle", position: "Position", duties: "Tätigkeiten",
    language: "Sprache", level: "Niveau", levelEmpty: "— auswählen —",
    certTitle: "Titel", certIssuedBy: "Ausstellende Organisation", certDate: "Datum", skill: "Fähigkeit",
    removeBtn: "Entfernen", saveBtn: "Lebenslauf speichern", savingBtn: "Wird gespeichert…",
    docsInfo: "Laden Sie für jeden Dokumenttyp aus der Liste unten Ihre Datei hoch. Bei einem neuen Upload desselben Typs wird die alte ersetzt.",
    docsEmpty: "Es wurden noch keine Dokumenttypen definiert.", docUploaded: "Hochgeladen:",
    docUploadBtn: "Datei auswählen", docUpdateBtn: "Aktualisieren", docUploading: "Wird hochgeladen…",
    statusApproved: "Bestätigt", statusRejected: "Abgelehnt", statusPending: "Wird geprüft",
    toastPhotoInvalid: "Sie können nur eine Bilddatei hochladen", toastPhotoUpdated: "Profilbild aktualisiert",
    toastSaved: "Lebenslauf gespeichert", toastDocUploaded: "Hochgeladen", toastDocDeleted: "Gelöscht",
  },
  en: {
    title: "My CV", subtitle: "Europass-style form — fill in all sections and save.", logout: "Logout",
    bannerPending: "Your profile is awaiting admin approval. Once approved, clinics will see it.",
    bannerApproved: "Your profile is approved.",
    tabCv: "CV", tabDocuments: "Documents", loading: "Loading…",
    sec1: "1. Personal information", sec2: "2. Education", sec3: "3. Work experience", sec4: "4. Languages", sec5: "5. Certificates", sec6: "6. Skills",
    photoLabel: "Profile photo", photoHint: "This photo appears when the CV is downloaded. Square / portrait photo recommended.",
    photoUploading: "Uploading…", photoChange: "Change photo", photoUpload: "Upload photo",
    name: "First name", surname: "Last name", gender: "Gender", genderEmpty: "— not selected —", genderMale: "Male", genderFemale: "Female",
    birthDate: "Date of birth", citizenship: "Citizenship", phone: "Phone", email: "Email", address: "Address",
    addBtn: "+ Add",
    emptyEducation: "No education added.", emptyWork: "No work experience added.",
    emptyLanguages: "No languages added.", emptyCertificates: "No certificates added.", emptySkills: "No skills added.",
    institution: "Institution", profession: "Specialty", professionEmpty: "— not selected —",
    startDate: "Start date", endDate: "End date", workplace: "Workplace", position: "Position", duties: "Duties",
    language: "Language", level: "Level", levelEmpty: "— select —",
    certTitle: "Title", certIssuedBy: "Issued by", certDate: "Date", skill: "Skill",
    removeBtn: "Remove", saveBtn: "Save CV", savingBtn: "Saving…",
    docsInfo: "Upload your file for each document type. Uploading a new file of the same type replaces the old one.",
    docsEmpty: "No document types defined yet.", docUploaded: "Uploaded:",
    docUploadBtn: "Choose file", docUpdateBtn: "Update", docUploading: "Uploading…",
    statusApproved: "Approved", statusRejected: "Rejected", statusPending: "Pending",
    toastPhotoInvalid: "Only image files can be uploaded", toastPhotoUpdated: "Profile photo updated",
    toastSaved: "CV saved", toastDocUploaded: "Uploaded", toastDocDeleted: "Deleted",
  },
  ru: {
    title: "Моё резюме", subtitle: "Форма Europass — заполните все разделы и сохраните.", logout: "Выйти",
    bannerPending: "Ваш профиль ожидает подтверждения администратора. После подтверждения его увидят клиники.",
    bannerApproved: "Ваш профиль подтверждён.",
    tabCv: "Резюме", tabDocuments: "Документы", loading: "Загрузка…",
    sec1: "1. Личные данные", sec2: "2. Образование", sec3: "3. Опыт работы", sec4: "4. Языки", sec5: "5. Сертификаты", sec6: "6. Навыки",
    photoLabel: "Фото профиля", photoHint: "При скачивании резюме отображается это фото. Рекомендуется квадратное / портретное.",
    photoUploading: "Загрузка…", photoChange: "Изменить фото", photoUpload: "Загрузить фото",
    name: "Имя", surname: "Фамилия", gender: "Пол", genderEmpty: "— не выбрано —", genderMale: "Мужской", genderFemale: "Женский",
    birthDate: "Дата рождения", citizenship: "Гражданство", phone: "Телефон", email: "Email", address: "Адрес",
    addBtn: "+ Добавить",
    emptyEducation: "Образование не добавлено.", emptyWork: "Опыт работы не добавлен.",
    emptyLanguages: "Языки не добавлены.", emptyCertificates: "Сертификаты не добавлены.", emptySkills: "Навыки не добавлены.",
    institution: "Учреждение", profession: "Специальность", professionEmpty: "— не выбрано —",
    startDate: "Дата начала", endDate: "Дата окончания", workplace: "Место работы", position: "Должность", duties: "Обязанности",
    language: "Язык", level: "Уровень", levelEmpty: "— выберите —",
    certTitle: "Название", certIssuedBy: "Организация", certDate: "Дата", skill: "Навык",
    removeBtn: "Удалить", saveBtn: "Сохранить резюме", savingBtn: "Сохранение…",
    docsInfo: "Загрузите файл для каждого типа документа. Новый файл того же типа заменит старый.",
    docsEmpty: "Типы документов ещё не определены.", docUploaded: "Загружено:",
    docUploadBtn: "Выбрать файл", docUpdateBtn: "Обновить", docUploading: "Загрузка…",
    statusApproved: "Подтверждено", statusRejected: "Отклонено", statusPending: "Ожидает",
    toastPhotoInvalid: "Можно загрузить только файл изображения", toastPhotoUpdated: "Фото профиля обновлено",
    toastSaved: "Резюме сохранено", toastDocUploaded: "Загружено", toastDocDeleted: "Удалено",
  },
  tr: {
    title: "Özgeçmişim", subtitle: "Europass tarzı form — tüm bölümleri doldurun ve kaydedin.", logout: "Çıkış",
    bannerPending: "Profiliniz yönetici onayı bekliyor. Onaylandıktan sonra klinikler görecek.",
    bannerApproved: "Profiliniz onaylandı.",
    tabCv: "Özgeçmiş", tabDocuments: "Belgeler", loading: "Yükleniyor…",
    sec1: "1. Kişisel bilgiler", sec2: "2. Eğitim", sec3: "3. İş deneyimi", sec4: "4. Dil bilgisi", sec5: "5. Sertifikalar", sec6: "6. Beceriler",
    photoLabel: "Profil fotoğrafı", photoHint: "Özgeçmiş indirildiğinde bu fotoğraf görünür. Kare / portre fotoğraf önerilir.",
    photoUploading: "Yükleniyor…", photoChange: "Fotoğrafı değiştir", photoUpload: "Fotoğraf yükle",
    name: "Ad", surname: "Soyad", gender: "Cinsiyet", genderEmpty: "— seçilmedi —", genderMale: "Erkek", genderFemale: "Kadın",
    birthDate: "Doğum tarihi", citizenship: "Uyruk", phone: "Telefon", email: "E-posta", address: "Adres",
    addBtn: "+ Ekle",
    emptyEducation: "Eğitim eklenmedi.", emptyWork: "İş deneyimi eklenmedi.",
    emptyLanguages: "Dil eklenmedi.", emptyCertificates: "Sertifika eklenmedi.", emptySkills: "Beceri eklenmedi.",
    institution: "Kurum", profession: "Uzmanlık", professionEmpty: "— seçilmedi —",
    startDate: "Başlangıç", endDate: "Bitiş", workplace: "İş yeri", position: "Pozisyon", duties: "Görevler",
    language: "Dil", level: "Seviye", levelEmpty: "— seçin —",
    certTitle: "Başlık", certIssuedBy: "Veren kurum", certDate: "Tarih", skill: "Beceri",
    removeBtn: "Sil", saveBtn: "Özgeçmişi kaydet", savingBtn: "Kaydediliyor…",
    docsInfo: "Aşağıdaki listeden her belge türü için kendi dosyanızı yükleyin. Aynı türden yeni dosya eskisini değiştirir.",
    docsEmpty: "Henüz belge türü tanımlanmadı.", docUploaded: "Yüklenmiş:",
    docUploadBtn: "Dosya seç", docUpdateBtn: "Güncelle", docUploading: "Yükleniyor…",
    statusApproved: "Onaylı", statusRejected: "Reddedildi", statusPending: "Bekliyor",
    toastPhotoInvalid: "Yalnızca resim dosyası yükleyebilirsiniz", toastPhotoUpdated: "Profil fotoğrafı güncellendi",
    toastSaved: "Özgeçmiş kaydedildi", toastDocUploaded: "Yüklendi", toastDocDeleted: "Silindi",
  },
};

export default {
  name: "ProfileNursePage",
  data() {
    return {
      form: {
        name: "", surname: "", gender: "", birthDate: "", citizenship: "", phone: "", address: "",
        isApproved: false,
        educations: [],
        workExperiences: [],
        languages: [],
        certificates: [],
        skills: [],
      },
      photoUrl: "",
      photoUploading: false,
      loading: true,
      saving: false,
      activeTab: "cv",
      documentTypes: [],
      myDocuments: [],
      uploadingType: null,
    };
  },
  computed: {
    email() { return this.$store.state.auth?.email || ""; },
    professions() { return this.$store.state.professions || []; },
    lang() { return this.$store.state.lang || "az"; },
  },
  mounted() {
    if (!this.professions.length) this.$store.dispatch("getProfessions");
    this.load();
    this.loadDocs();
  },
  methods: {
    tx(key) {
      const sKey = "ProfileNurse" + key.charAt(0).toUpperCase() + key.slice(1);
      const ov = this.$store.state.statics && this.$store.state.statics[sKey];
      if (ov && String(ov).trim()) return ov;
      const d = T[this.lang] || T.az;
      return d[key] != null ? d[key] : (T.az[key] || key);
    },
    async load() {
      this.loading = true;
      try {
        const res = await axios.get(`/nurse/profile`);
        const p = res.data?.data || {};
        this.photoUrl = p.photoUrl || "";
        this.form = {
          name: p.name || "",
          surname: p.surname || "",
          gender: p.gender || "",
          birthDate: isoDate(p.birthDate) || "",
          citizenship: p.citizenship || "",
          phone: p.phone || "",
          address: p.address || "",
          isApproved: !!p.isApproved,
          educations: (p.educations || []).map(e => ({
            institutionName: e.institutionName || "",
            professionId: e.professionId || null,
            startDate: isoDate(e.startDate) || "",
            endDate: isoDate(e.endDate) || "",
          })),
          workExperiences: (p.workExperiences || []).map(w => ({
            workplace: w.workplace || "",
            position: w.position || "",
            startDate: isoDate(w.startDate) || "",
            endDate: isoDate(w.endDate) || "",
            duties: w.duties || "",
          })),
          languages: (p.languages || []).map(l => ({ languageName: l.languageName || "", level: l.level || "" })),
          certificates: (p.certificates || []).map(c => ({
            title: c.title || "", issuedBy: c.issuedBy || "", issuedDate: isoDate(c.issuedDate) || "",
          })),
          skills: (p.skills || []).map(s => ({ description: s.description || "" })),
        };
      } finally {
        this.loading = false;
      }
    },
    async onPhotoChange(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        this.$toast.error(this.tx('toastPhotoInvalid'));
        ev.target.value = "";
        return;
      }
      this.photoUploading = true;
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`/nurse/profile/photo`, fd);
        this.photoUrl = res.data?.data || this.photoUrl;
        this.$toast.success(this.tx('toastPhotoUpdated'));
      } catch (e) {
        // toast by interceptor
      } finally {
        this.photoUploading = false;
        if (this.$refs.photoInput) this.$refs.photoInput.value = "";
      }
    },
    triggerPhoto() { if (this.$refs.photoInput) this.$refs.photoInput.click(); },
    addEducation() { this.form.educations.push(emptyEducation()); },
    addWork() { this.form.workExperiences.push(emptyWork()); },
    addLanguage() { this.form.languages.push(emptyLanguage()); },
    addCertificate() { this.form.certificates.push(emptyCertificate()); },
    addSkill() { this.form.skills.push(emptySkill()); },
    async save() {
      this.saving = true;
      try {
        const payload = {
          name: this.form.name,
          surname: this.form.surname,
          gender: this.form.gender || null,
          birthDate: this.form.birthDate || null,
          citizenship: this.form.citizenship || null,
          phone: this.form.phone || null,
          address: this.form.address || null,
          educations: this.form.educations.map(e => ({
            institutionName: e.institutionName,
            professionId: e.professionId || null,
            startDate: e.startDate || null,
            endDate: e.endDate || null,
          })),
          workExperiences: this.form.workExperiences.map(w => ({
            workplace: w.workplace,
            position: w.position,
            startDate: w.startDate || null,
            endDate: w.endDate || null,
            duties: w.duties || null,
          })),
          languages: this.form.languages.map(l => ({ languageName: l.languageName, level: l.level })),
          certificates: this.form.certificates.map(c => ({
            title: c.title,
            issuedBy: c.issuedBy || null,
            issuedDate: c.issuedDate || null,
            fileId: null,
          })),
          skills: this.form.skills.map(s => ({ description: s.description })),
        };
        await axios.put(`/nurse/profile`, payload);
        this.$toast.success(this.tx('toastSaved'));
        await this.load();
      } catch (e) {
        // toast
      } finally {
        this.saving = false;
      }
    },
    logout() {
      this.$store.dispatch("auth/logout");
      this.$router.push({ name: "home" });
    },
    async loadDocs() {
      try {
        const a = await axios.get(`/home/document-types`);
        this.documentTypes = a.data?.data || [];
      } catch {
        this.documentTypes = [];
      }
      try {
        const b = await axios.get(`/nurse/documents`);
        this.myDocuments = b.data?.data || [];
      } catch {
        this.myDocuments = [];
      }
    },
    currentDocFor(typeId) {
      return this.myDocuments.find((d) => d.documentTypeId === typeId) || null;
    },
    statusLabel(s) {
      if (s === "Approved") return this.tx('statusApproved');
      if (s === "Rejected") return this.tx('statusRejected');
      return this.tx('statusPending');
    },
    statusClass(s) {
      if (s === "Approved") return "approved";
      if (s === "Rejected") return "rejected";
      return "pending";
    },
    async onDocFile(ev, typeId) {
      const file = ev.target.files && ev.target.files[0];
      if (!file) return;
      this.uploadingType = typeId;
      try {
        const fd = new FormData();
        fd.append("DocumentTypeId", String(typeId));
        fd.append("File", file);
        await axios.post(`/nurse/documents`, fd);
        this.$toast.success(this.tx('toastDocUploaded'));
        await this.loadDocs();
      } catch (e) {
        // toast by interceptor
      } finally {
        this.uploadingType = null;
        ev.target.value = "";
      }
    },
    async removeDoc(id) {
      try {
        await axios.delete(`/nurse/documents/${id}`);
        this.$toast.success(this.tx('toastDocDeleted'));
        await this.loadDocs();
      } catch (e) {
        // toast
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-page { min-height: 60vh; }
.profile-card {
  background: #fff; border-radius: 12px; padding: 2rem; box-shadow: 0 2px 14px rgba(0,0,0,0.06);
  .head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1rem; gap: 1rem;
    h2 { color: #1e3c72; margin: 0; }
    .muted { color: #64748b; font-size: 0.85rem; margin-top: 0.25rem; }
    .ghost { background: #fff; border: 1px solid #d0d5dd; border-radius: 6px; padding: 0.45rem 0.9rem; color: #475569; cursor: pointer; font: inherit; font-size: 0.85rem; &:hover { background: #f7f9fc; } }
  }
  .banner { padding: 0.7rem 1rem; border-radius: 8px; margin-bottom: 1.25rem; font-size: 0.88rem;
    &.pending { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
    &.approved { background: #e6f6ed; color: #1e7d44; border: 1px solid #b6e5c8; }
  }
  .status { background: #f8fafc; padding: 1.25rem; text-align: center; color: #666; border-radius: 8px; }
}

.section {
  margin-bottom: 1.75rem; padding: 1.25rem; background: #f8fafc; border-radius: 10px; border: 1px solid #eef0f4;
  h3 { color: #1e3c72; margin: 0 0 0.75rem; font-size: 1rem; }
  .section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem;
    h3 { margin: 0; }
    .add { background: #fff; color: #2a5298; border: 1px solid #c5d2e8; border-radius: 6px; padding: 0.4rem 0.85rem; cursor: pointer; font: inherit; font-size: 0.82rem; font-weight: 600;
      &:hover { background: #eef3fb; }
    }
  }
  .empty { color: #94a3b8; font-style: italic; font-size: 0.85rem; padding: 0.5rem 0; }
  .row-block { background: #fff; padding: 1rem; border-radius: 8px; border: 1px solid #eef0f4; margin-bottom: 0.75rem; position: relative;
    .remove { position: absolute; top: 0.5rem; right: 0.5rem; background: transparent; border: 0; color: #c0392b; font: inherit; font-size: 0.78rem; cursor: pointer;
      &:hover { text-decoration: underline; }
    }
  }
}
.photo-row { display: flex; align-items: center; gap: 1.25rem; margin-bottom: 1.1rem; padding-bottom: 1.1rem; border-bottom: 1px solid #eef0f4;
  .photo-preview { width: 92px; height: 112px; border-radius: 8px; overflow: hidden; flex-shrink: 0; background: linear-gradient(135deg,#e6f0ff,#d6e4fb); display: flex; align-items: center; justify-content: center;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .ph { color: #2a5298; font-weight: 700; font-size: 1.5rem; letter-spacing: 1px; }
  }
  .photo-actions { display: flex; flex-direction: column; gap: 0.3rem;
    .photo-label { font-size: 0.85rem; font-weight: 700; color: #1e3c72; }
    .photo-hint { font-size: 0.78rem; color: #64748b; max-width: 360px; }
    .hidden-file { display: none; }
    .photo-btn { align-self: flex-start; margin-top: 0.35rem; background: #2a5298; color: #fff; border: 0; border-radius: 6px; padding: 0.5rem 1rem; cursor: pointer; font: inherit; font-size: 0.85rem; font-weight: 600;
      &:hover:not(:disabled) { background: #1e3c72; }
      &:disabled { opacity: 0.6; cursor: not-allowed; }
    }
  }
  @media (max-width: 575px) { flex-direction: column; align-items: flex-start; }
}
.grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem;
  @media (max-width: 575px) { grid-template-columns: 1fr; }
}
.field { display: flex; flex-direction: column; gap: 0.3rem; margin-bottom: 0.6rem;
  &.full { grid-column: 1 / -1; }
  span { font-size: 0.78rem; color: #475569; font-weight: 600; }
  input, textarea, select { padding: 0.55rem 0.75rem; border: 1px solid #d0d5dd; border-radius: 6px; font: inherit; outline: none; resize: vertical; background: #fff;
    &:focus { border-color: #2a5298; }
    &:disabled { background: #f7f9fc; color: #64748b; }
  }
}
.save-btn { width: 100%; padding: 0.85rem; background: #2a5298; color: #fff; border: 0; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; font-size: 1rem; margin-top: 0.5rem;
  &:hover:not(:disabled) { background: #1e3c72; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.tabs { display: flex; gap: 0.25rem; border-bottom: 1px solid #e2e8f0; margin: 1rem 0 1.5rem;
  button { padding: 0.65rem 1.25rem; background: transparent; border: 0; border-bottom: 3px solid transparent; font: inherit; font-size: 0.92rem; color: #64748b; cursor: pointer; font-weight: 600;
    &:hover { color: #1e3c72; }
    &.active { color: #1e3c72; border-bottom-color: #2a5298; }
  }
}
.docs-tab {
  .small-info { font-size: 0.85rem; color: #64748b; margin-bottom: 1rem; }
  .docs-list { list-style: none; padding: 0; margin: 0; }
  .doc-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem; background: #f8fafc; border: 1px solid #eef0f4; border-radius: 10px; margin-bottom: 0.75rem; flex-wrap: wrap;
    .info { flex: 1; min-width: 0;
      strong { color: #1e3c72; }
      .muted { color: #64748b; }
      .small { font-size: 0.82rem; }
      .title-row { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
      .status-badge { display: inline-block; padding: 0.15rem 0.55rem; border-radius: 999px; font-size: 0.72rem; font-weight: 600;
        &.pending { background: #fef3c7; color: #92400e; }
        &.approved { background: #e6f6ed; color: #1e7d44; }
        &.rejected { background: #fdecea; color: #c0392b; }
      }
      .existing { margin-top: 0.4rem; font-size: 0.85rem; color: #475569;
        a { color: #2a5298; text-decoration: none; margin-left: 0.3rem; word-break: break-all;
          &:hover { text-decoration: underline; }
        }
        .link-danger { background: transparent; border: 0; color: #c0392b; font: inherit; font-size: 0.82rem; cursor: pointer; margin-left: 0.6rem;
          &:hover { text-decoration: underline; }
        }
      }
      .admin-note { margin-top: 0.4rem; padding: 0.5rem 0.75rem; background: #fff; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 0.85rem; color: #334155;
        .lbl { font-weight: 600; color: #475569; margin-right: 0.4rem; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.04em; }
      }
    }
    .upload-btn { display: inline-block; padding: 0.5rem 1rem; background: #2a5298; color: #fff; border-radius: 6px; font-size: 0.85rem; font-weight: 600; cursor: pointer;
      input { display: none; }
      &:hover { background: #1e3c72; }
    }
  }
}
</style>
